<template>
  <div id="event-container" class="event-container" ref="evtContainer" v-if="getCurrentEvent">

    <div class="event-header">
      <div class="event-logo-back">
        <img :src="getCurrentEvent.appImage">
      </div>
      <div class="event-details" :class="{ 'no-chevron': !isFullApp }">
        <fa :icon="['fas', 'chevron-left']" @click="backArrow" v-if="isFullApp" />
        <div v-else class="spacer" />
        <div class="label">
          <h2 v-text="getCurrentEvent.name"></h2>
          <span v-if="getCurrentEvent.eventDate" v-text="getCurrentEvent.eventDate"></span>
          <span v-else v-text="`${getCurrentEvent.startDate} - ${getCurrentEvent.endDate}`"></span>
          <span v-text="getCurrentEvent.desc"></span>

          <div class="socio-details">
            <div v-if="getUser?.contractName" class="plan-name">{{ getUser?.contractName }}</div>
            <div v-else class="plan-name">NÃO ASSOCIADO</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Header with tags container -->
    <FloatingHeader
      @sendRef="handleRefs"
      @tagClicked="(groupCode, index) => scrollToSection(groupCode, index)"
      :showHeader="showHeader"
      @search="onSearchMenu"
      :title="getCurrentEvent.name"
      :sbtitle="getCurrentEvent.eventDate ? getCurrentEvent.eventDate : `${getCurrentEvent.startDate} - ${getCurrentEvent.endDate}`"
      :tags="tags"
      :hideArrowBack="!isFullApp"
    >
    </FloatingHeader>

    <!-- Event Body with Food Menu -->
    <div class="event-body">
      <div v-if="getUser?.contractName && getMenu?.consumerType !== 'T' && !discountHidden" class='discount-applied'>
        <fa :icon="['fas', 'tag']" />
        Desconto aplicado: {{ getUser?.contractName }}
      </div>
      <FoodMenu @productClicked="handleProduct" :search="search" @found="hideDiscount"></FoodMenu>
    </div>

    <!-- Floating Components, Modals, Screen Loader -->
    <FloatingCart></FloatingCart>
    <ModalInfo @cleanCartAndBack="cleanCartAndBack"></ModalInfo>
    <ModalProdSelection @modalProductSelection="modalProductSelection"></ModalProdSelection>
    <MicButton :onAudioRecorded="handleAudio" :forceStatus="micButtonStatus" v-if="false"></MicButton>
    <ScreenLoader :isLoading="isLoading" :loadingText="loadingText"></ScreenLoader>

  </div>
</template>

<script>
import ProductController from '@/assets/controllers/ProductController'
import Util from '@/assets/libs/util'
import FloatingCart from '@/components/FloatingCart'
import FloatingHeader from '@/components/FloatingHeader.vue'
import FoodMenu from '@/components/FoodMenu.vue'
import MicButton from '@/components/buttons/MicButton.vue'
import ScreenLoader from '@/components/loader/ScreenLoader.vue'
import ModalInfo from '@/components/modals/ModalInfo.vue'
import ModalProdSelection from '@/components/modals/ModalProdSelection.vue'

import { refreshUserStatus } from '@/assets/controllers/LoginController'
import { loadMenu } from '@/assets/controllers/MenuController'
import eventModule from '@/assets/libs/eventModule'
import requestModule from '@/assets/libs/requestModule'
import * as lame from '@breezystack/lamejs'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'EventView',
  components: {
    FoodMenu,
    FloatingHeader,
    ModalInfo,
    ModalProdSelection,
    FloatingCart,
    MicButton,
    ScreenLoader
  },
  data () {
    return {
      showHeader: false,
      curTimeout: null,
      search: '',
      tags: [],
      micButtonStatus: null,
      modal: {
        text: 'Voltar irá limpar o carrinho, deseja prosseguir?',
        icon: 'fa-exclamation-triangle',
        buttons: [
          {
            buttonText: 'Cancelar',
            buttonAction: ''
          },
          {
            buttonText: 'Limpar',
            buttonAction: 'cleanCartAndBack'
          }
        ],
        open: false
      },
      isLoading: false,
      loadingText: '',
      discountHidden: false
    }
  },
  computed: {
    ...mapGetters(['getCurrentEvent', 'getMenu', 'getCartLength', 'getFirstGroupIten', 'isFullApp', 'getEvents', 'getUser'])
  },
  methods: {
    ...mapActions([
      'updateCurrentItemEdit',
      'showModalInfo',
      'showModalProdSelection',
      'resetCart',
      'addToCart',
      'addGroupItens',
      'removeGroupItens',
      'updateSelectedEvent',
      'loadEvents'
    ]),
    async compressAudio (audioBlob) {
      const audioContext = new AudioContext()

      // Convert blob to ArrayBuffer
      const arrayBuffer = await audioBlob.arrayBuffer()

      // Decode the audio
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

      // (Optional) Downsample to 16 kHz using OfflineAudioContext
      const offlineContext = new OfflineAudioContext(
        1,
        audioBuffer.duration * 16000,
        16000
      )
      const source = offlineContext.createBufferSource()
      source.buffer = audioBuffer
      source.connect(offlineContext.destination)
      source.start(0)

      const renderedBuffer = await offlineContext.startRendering()

      // Encode to MP3
      return this.encodeToMP3(renderedBuffer)
    },
    encodeToMP3 (audioBuffer) {
      // Only grab channel 0 if in mono
      const samples = audioBuffer.getChannelData(0)
      const mp3Encoder = new lame.Mp3Encoder(1, 16000, 64) // mono, 16 kHz, 64 kbps
      // Convert from [-1, 1] float range to 16-bit signed PCM
      const sampleBlockSize = 1152
      const mp3Data = []
      const convertedSamples = new Int16Array(samples.length)

      for (let i = 0; i < samples.length; i++) {
        convertedSamples[i] = Math.max(-1, Math.min(1, samples[i])) * 32767
      }

      // Encode in small blocks
      for (let i = 0; i < convertedSamples.length; i += sampleBlockSize) {
        const chunk = convertedSamples.subarray(i, i + sampleBlockSize)
        const mp3buf = mp3Encoder.encodeBuffer(chunk)
        if (mp3buf.length > 0) {
          mp3Data.push(mp3buf)
        }
      }

      // Flush final MP3 frame
      const finalChunk = mp3Encoder.flush()
      if (finalChunk.length > 0) {
        mp3Data.push(finalChunk)
      }

      // Combine all chunks into a Blob
      return new Blob(mp3Data, { type: 'audio/mpeg' })
    },
    async handleAudio (audioBlob) {
      const sf = this
      // const audioUrl = URL.createObjectURL(audioBlob)
      // const audioElement = new Audio(audioUrl)
      this.micButtonStatus = 'processing'
      await requestModule.sendJsonAndAudio(this.getMenu, await this.compressAudio(audioBlob)).then(aiItens => {
        this.micButtonStatus = null
        aiItens = JSON.parse([...new Set(aiItens.split('\n'))]?.[0])
        const products = aiItens.codes.map(c => c.toString())
        if (products.length) {
          this.showModalProdSelection({ open: true, products })
        } else {
          throw new Error('Nenhum produto encontrado')
        }
      }).catch(e => {
        sf.micButtonStatus = 'error'
        setTimeout(() => {
          sf.micButtonStatus = null
        }, 1800)
        console.log(e)
      })
      // audioElement.play()
    },
    cleanCartAndBack: function () {
      this.resetCart()
      this.backArrow()
    },
    onSearchMenu: function (searchOption) {
      eventModule.emit('searchInMenu', searchOption.text)
    },
    backArrow: function () {
      if (!this.getCartLength) {
        this.$router.push('/')
      } else {
        this.modal.open = true
        this.showModalInfo(this.modal)
      }
    },
    scrollToSection (groupCode, index) {
      const container = this.$refs.evtContainer
      const targetEl = document.getElementById(`group-${index}`) || document.getElementById(groupCode)

      if (container && targetEl) {
        container.scrollTo({
          top: targetEl.offsetTop + 80,
          behavior: 'smooth'
        })
      }
    },
    onScroll: function () {
      if (this.curTimeout) clearTimeout(this.curTimeout)

      const container = this.$refs.evtContainer
      this.showHeader = container.scrollTop > 80

      const groups = Object.values(document.getElementsByClassName('group-container'))
      const currentGroup = groups.find((g, idx) =>
        (container.scrollTop + 80) <= (g.offsetTop + g.offsetHeight)
      )

      const activeIndex = groups.indexOf(currentGroup)
      const tagContainer = this.childrenRefs.tagContainer

      this.curTimeout = setTimeout(() => {
        this.tags.forEach((tag, i) => {
          tag.active = i === activeIndex

          if (i === activeIndex) {
            const refEl = this.childrenRefs[`tag${tag.groupCode}`]?.[0]
            const left = refEl?.offsetLeft || 0

            tagContainer?.scrollTo({
              left: Math.max(0, left - 16),
              behavior: 'smooth'
            })
          }
        })
      }, 100)
    },
    handleProduct: function (prod) {
      if (prod) {
        this.updateCurrentItemEdit(ProductController.ajustProductToOpen(JSON.parse(JSON.stringify(prod))))
        this.removeGroupItens()
        this.$router.push('productitem')
      }
    },
    modalProductSelection: function (prods) {
      const sf = this
      prods.forEach(prod => {
        const prodToCart = ProductController.ajustProductToOpen(JSON.parse(JSON.stringify(prod)))
        if (prodToCart?.productGroups?.length) {
          sf.addGroupItens(prod)
        } else {
          sf.addToCart(prodToCart)
        }
      })
      this.handleProduct(this.getFirstGroupIten?.[0])
    },
    handleRefs: function (refs) {
      this.childrenRefs = refs
    },
    checkRedirect: async function () {
      if (this.isFullApp) {
        this.$router.push('/')
        return
      }

      // logica mini app - verifica se o evento existe, caso contrario, busca o evento
      if (!this.getEvents || this.getEvents.length === 0) {
        this.isLoading = true
        this.loadingText = 'Carregando eventos...'
        await this.loadEvents()
        this.isLoading = false
      }

      const event = this.getEvents?.[0] || null // pega o primeiro evento, que é o padrão
      if (!event) {
        Util.showErrorNotification({
          title: 'Evento não encontrado',
          text: 'Por favor, tente novamente.'
        })
        this.$router.push('/')
        return
      }

      this.updateSelectedEvent(event)
      this.$router.push('/event')
    },
    hideDiscount: function (found) {
      if (found) {
        this.discountHidden = false
      } else {
        this.discountHidden = true
      }
    }
  },
  async mounted () {
    // verifica se o usuário está logado e se o status do plano mudou
    const statusHasChanged = await refreshUserStatus()
    if (statusHasChanged) {
      this.isLoading = true
      this.loadingText = 'Atualizando cardápio...'
      const result = await loadMenu()
      if (!result && this.getUser?.consumerType !== 'T') {
        const forceDefaultMenu = true
        const defaultMenuResult = await loadMenu(forceDefaultMenu)

        // caso tenha aparecido o menu padrão, avisar ao usuario que os descontos não serão aplicados
        if (defaultMenuResult) {
          this.showModalInfo({
            type: 'error',
            text: 'Não foi possível carregar os descontos do seu plano. O menu padrão será exibido. Tente novamente ou contate o suporte.',
            icon: 'fa-solid fa-exclamation-triangle',
            open: true
          })
        }
      }
      this.isLoading = false
    }

    // Verifica se o menu e o evento estão prontos
    const menuOk = this.getMenu && Array.isArray(this.getMenu.groups) && this.getMenu.groups.length > 0
    const eventOk = this.getCurrentEvent

    if (!menuOk) {
      Util.showErrorNotification({
        title: 'Cardápio não encontrado ou expirado',
        text: 'Por favor, tente novamente.'
      })
      this.$router.push('/')
      return
    }

    if (!eventOk) {
      this.checkRedirect()
      return
    }

    // Se chegou até aqui, evento e menu estão prontos
    this.tags = this.getMenu.groups.map((g, i) => ({
      groupCode: g.groupCode,
      groupName: g.groupName,
      index: i,
      active: i === 0
    }))

    this.$refs.evtContainer?.addEventListener('scroll', this.onScroll)

    const firstProduct = this.getFirstGroupIten?.[0]
    if (firstProduct) {
      this.handleProduct(firstProduct)
    }
  },
  beforeUnmount () {
    if (this.getCurrentEvent) {
      this.$refs.evtContainer?.removeEventListener('scroll', this.onScroll)
    }
  }
}
</script>

<style scoped lang="less">
.event-container {
  position: relative;
  height: calc(var(--real-vh) - 30px);
  overflow: hidden auto;

  .event-header {
    .event-logo-back {
      position: fixed;
      object-fit: cover;
      height: 120px;
      width: 100%;
      right: 0;
      left: 0;
      top: 0;

      &:after {
        content: '';
        background: #000;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        height: 120px;
        display: block;
        opacity: 0.2;
      }

      img {
        object-fit: cover;
        height: 120px;
        width: 100%;
      }

      svg {
        position: absolute;
        color: var(--primaryColor);
        left: 20px;
        top: 50%;
        transform: translate(0, -100%);
        z-index: 10;
        font-size: 20px;
      }
    }

    .event-details {
      position: sticky;
      top: 0;
      background: white;
      margin: 100px 0 0 0;
      padding: 20px 20px 20px 20px;
      display: grid;
      grid-template-columns: 42px calc(100% - 62px);
      // grid-column-gap: 20px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;

      .spacer {
        width: 42px;
      }

      &.no-chevron {
        grid-template-columns: 0px 100%;
      }

      svg {
        position: relative;
        display: block;
        margin: auto 0;
        font-size: 20px;
        color: var(--primaryColor);
      }

      img {
        object-fit: cover;
        border-radius: 100%;
        display: block;
        height: 42px;
        width: 42px;
        margin: auto;
      }

      h2 {
        margin: 0;
        padding: 0;
        font-size: 20px;
      }

      span {
        display: block;
        font-size: 14px;
      }

      .socio-details {
        .plan-name {
          font-size: 14px;
          color: var(--secondaryColor);
        }
      }
    }
  }

  .event-body {
    background: white;
    position: relative;
    display: block;

    .discount-applied {
      margin: 0 20px 10px 20px;
      font-size: 14px;
      color: #7ec073;
      font-weight: 600;
    }
  }
}
</style>
